import { useEffect, useId, useRef, useState } from 'react';

export type NodeStatusInput = {
  connection: string;
  marketStatus: string;
  syncError: string;
  ready: boolean;
  catchingUp?: boolean;
};

export function footerNodeStatus({
  connection,
  marketStatus,
  syncError,
  ready,
  catchingUp,
}: NodeStatusInput) {
  if (syncError.trim())
    return { label: 'Unavailable', tone: 'error', detail: syncError };
  if (
    connection === 'disconnected' ||
    ['unavailable', 'offline', 'error'].includes(marketStatus)
  )
    return {
      label: 'Unavailable',
      tone: 'error',
      detail: 'Live data is currently unavailable. Retrying automatically.',
    };
  if (connection === 'reconnecting')
    return {
      label: 'Reconnecting',
      tone: 'pending',
      detail:
        'Connection to live data was interrupted. Reconnecting automatically.',
    };
  if (connection === 'connecting' || !ready || marketStatus === 'connecting')
    return {
      label: 'Connecting',
      tone: 'pending',
      detail: 'Connecting to live data. Waiting for a confirmed update.',
    };
  if (catchingUp)
    return {
      label: 'Recovering',
      tone: 'pending',
      detail:
        'Catching up on recent blockchain activity. Live data will update automatically.',
    };
  if (connection === 'connected' && marketStatus === 'live')
    return {
      label: 'Stable',
      tone: 'stable',
      detail: 'Live data is connected and up to date.',
    };
  return {
    label: 'Recovering',
    tone: 'pending',
    detail: 'Live data is recovering. Waiting for a confirmed update.',
  };
}

export function FooterNodeStatus(props: NodeStatusInput) {
  const status = footerNodeStatus(props);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const detailId = useId();
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  return (
    // The group keeps the tooltip open while the pointer moves from its button into the detail.
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className="app-footer-node"
      role="group"
      aria-label="Live data status"
      ref={root}
      onPointerEnter={(event) => {
        if (event.pointerType !== 'touch') setOpen(true);
      }}
      onPointerLeave={(event) => {
        if (
          event.pointerType !== 'touch' &&
          !button.current?.matches(':focus-visible')
        )
          setOpen(false);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          if (event.target !== button.current) button.current?.focus();
          setOpen(false);
        }
      }}
    >
      <button
        ref={button}
        type="button"
        className="app-footer-node-trigger"
        data-tone={status.tone}
        aria-label={'Live data: ' + status.label}
        aria-expanded={open}
        aria-controls={detailId}
        aria-describedby={open ? detailId : undefined}
        onFocus={(event) => {
          if (event.currentTarget.matches(':focus-visible')) setOpen(true);
        }}
        onClick={(event) =>
          setOpen((previous) => event.detail === 0 || !previous)
        }
      >
        <span className="app-footer-node-dot" aria-hidden="true" />
        <span>{status.label}</span>
      </button>
      {open && (
        <div className="app-footer-node-detail" role="tooltip" id={detailId}>
          <strong>Live data · {status.label}</strong>
          <span>{status.detail}</span>
        </div>
      )}
    </div>
  );
}
